
import Vue from "vue";
import Component from "vue-class-component";
import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
import {
  EndorsementsClient,
  OfficialEndorsementLightViewModel,
} from "@/api-client";
import ServersidePagingSelector from "@/components/ServersidePagingSelector.vue";
import { formatDate } from "@/formatting/dateFormatter";
import { BvTableFieldArray } from "bootstrap-vue";
import ClientsidePagingSelector from "@/components/ClientsidePagingSelector.vue";
import Loader from "@/features/shared/components/Loader";
import ServersidePaging from "@/components/ServersidePaging";
import { debounceFn } from "debounce-decorator-ts";
import PopoverInfo from "@/components/PopoverInfo.vue";
import FilterCheckbox from "@/components/FilterCheckbox.vue";
import EndorsementStatusBadge from "./EndorsementStatusBadge.vue";

class Filters {
  searchTerm: string;
  showInvalid!: boolean;
  showValid!: boolean;
  showSuspended!: boolean;

  constructor(
    searchTerm?: string,
    showInvalid?: boolean,
    showValid?: boolean,
    showSuspended?: boolean
  ) {
    this.searchTerm = searchTerm || "";
    this.showInvalid = showInvalid || false;
    this.showValid = showValid || true;
    this.showSuspended = showSuspended || false;
  }
}

@Component({
  components: {
    PopoverInfo,
    TableEmptySlot,
    EndorsementStatusBadge,
    ClientsidePagingSelector,
    ServersidePagingSelector,
    FilterCheckbox,
  },
})
export default class EndorsementTable extends Vue {
  endorsements: OfficialEndorsementLightViewModel[] = [];
  dataLoaded = false;
  filters = new Filters();
  paging = new ServersidePaging();
  loader = new Loader(true);

  async created() {
    this.getParametersFromRouteOrDefault();

    this.$watch(
      () => this.filters,
      () => {
        this.paging.pageNumber = 1;
        this.reflectParametersToRoute();
      },
      { deep: true }
    );

    this.$watch(
      () => this.paging.pageNumber,
      () => {
        this.reflectParametersToRoute();
      }
    );

    this.$watch(
      () => this.paging.pageSize,
      () => {
        this.paging.pageNumber = 1;
        this.reflectParametersToRoute();
      }
    );

    this.$watch(
      () => this.$route.query,
      () => {
        this.getEndorsements();
      },
      { deep: true, immediate: true }
    );
  }


  navigateToDetails(endorsement: OfficialEndorsementLightViewModel) {
      this.$router.push({ name: 'endorsement-details', params: { id: endorsement.id, seafarerId: endorsement.seafarerId,} });
  }

  async getEndorsements() {
    this.endorsements = [];
    const result = await new EndorsementsClient()
      .setLoader(this.loader)
      .officialEndorsements(
        this.filters.searchTerm,
        this.paging.pageNumber,
        this.paging.pageSize,
        this.filters.showValid,
        this.filters.showInvalid,
        this.filters.showSuspended,
      );
    this.paging.totalCount = result.totalCount;
    this.paging.pageSize = result.pageSize;
    this.endorsements = result.pageResult;
    this.dataLoaded = true;
  }

  @debounceFn(100) // Prevents possible double-update
  reflectParametersToRoute(): void {
    this.$router.replace({
      query: {
        // Setting to 'undefined' removes parameter from URL.
        searchTerm: this.filters.searchTerm || undefined,
        pageNumber: this.paging.pageNumber
          ? this.paging.pageNumber.toString()
          : undefined,
        pageSize: this.paging.pageSize
          ? this.paging.pageSize.toString()
          : undefined,
        showInvalid: this.filters.showInvalid.toString(),
        showValid: this.filters.showValid.toString(),
        showSuspended: this.filters.showSuspended.toString(),
      },
    });
  }

  getParametersFromRouteOrDefault() {
    const route = this.$route as any;
    const searchTerm: string | undefined = route.query.searchTerm;
    const showInvalid: boolean | undefined = route.query.showInvalid;
    const showValid: boolean | undefined = route.query.showValid;
    const showSuspended: boolean | undefined = route.query.showSuspended;

    this.filters = new Filters(searchTerm, showInvalid, showValid, showSuspended);

    this.paging.pageNumber =
      parseInt(route.query.pageNumber) || this.paging.pageNumber;
    this.paging.pageSize =
      parseInt(route.query.pageSize) || this.paging.pageSize;
  }

  fields: BvTableFieldArray = [
    {
      key: "documentNumber",
      label: this.$ls.Global_Label_DocumentNumber,
    },
    {
      key: "seafarerName",
      label: this.$ls.Global_Label_Seafarer,
    },
    {
      key: "birthDate",
      label: this.$ls.Global_Label_DateOfBirth,
      formatter: (
        value: any,
        key: string,
        item: OfficialEndorsementLightViewModel
      ): string => formatDate(item.birthDate),
    },
    {
      key: "name",
      label: this.$ls.Global_Label_Name,
      thClass: "w-20",
    },
    {
      key: "issuedAt",
      label: this.$ls.Global_Label_IssueDateShort,
      formatter: (
        value: any,
        key: string,
        item: OfficialEndorsementLightViewModel
      ): string => formatDate(item.issuedAt),
    },
    {
      key: "validUntil",
      label: this.$ls.Global_Label_ValidUntil,
      formatter: (
        value: any,
        key: string,
        item: OfficialEndorsementLightViewModel
      ): string => formatDate(item.validUntil),
    },
    {
      key: "statusString",
      label: this.$ls.Global_Label_DocumentState,
    },
    {
      key: "linkIcon",
      label: "",
      thClass: "w-5",
      tdClass: "table-actions",
    },
  ];
}
